/**
 * YOJO Search Overlay
 * Premium dark overlay with predictive search.
 * Uses Shopify's predictive search API which returns pre-rendered,
 * server-side sanitized HTML from helper-predictive-search.liquid.
 *
 * UX-audit 2026-07-02 PR-F (#1 proper), upgrading the PR-A/#223 minimal
 * fallback link: when the suggest endpoint fails (the live platform 417,
 * any non-200, or a network error) we fall back to the Section Rendering
 * API on the healthy /search route (GET /search?q=X&section_id=main-search,
 * live-verified 200) and render the top results as rows in the overlay,
 * keeping the "all results" link on top. The #223 link + hint remains the
 * floor for the path where the fallback fetch itself fails, and for the
 * 0-results case.
 *
 * Hardening:
 * - 300ms debounce (audit §4 fix-depth #1: the SRA fallback adds a request
 *   per failed keystroke, keep the rate down).
 * - Monotonic request token: every async continuation checks its token
 *   against the latest dispatch and drops stale/out-of-order responses.
 * - Last-query cache for the SRA fallback (same query never refetches).
 * - All user-derived text via textContent / encodeURIComponent, never
 *   innerHTML interpolation (XSS).
 * - Result counts / states are announced through a dedicated
 *   visually-hidden role="status" element ([data-search-status]) instead
 *   of a live region on the whole results container, so screen readers
 *   hear a short summary rather than every injected node.
 *
 * KNOWN LIMITATION (live-verified 2026-07-02): options[prefix]=last does
 * NOT rescue partial Hebrew words on /search. The fallback simply shows
 * whatever /search returns for the typed query — no partial-word support
 * is claimed anywhere in the UI copy.
 *
 * SOLD-OUT ORDER (owner decision 2026-09-26, audit pass 2 item 2): every
 * request this file makes to /search, and the "all results" link, carries
 * options[unavailable_products]=show (UNAVAILABLE_PARAM). Shopify's default
 * is "last", which pushed an exact sold-out match to the bottom: "תות" put
 * frozen strawberry at #48 of 54 and out of the overlay's six rows, so a
 * shopper never learned we carry it. With "show" results keep relevance
 * order; a sold-out row says so (the card's own badge text) and its product
 * page carries the back-in-stock form. The suggest endpoint gets the same
 * option in its own resources[options] form. The session probe in open() is
 * deliberately unchanged: it only asks whether the endpoint answers at all.
 *
 * SWARM 2026-10-01 (lane search-seo, audit/website-swarm-2026-10-01):
 * - a03-F6 / H16: fallback rows carry the card's own price line, read from the
 *   .yhp-price node the /search render already has ("21 ₪ · 0.5 ק״ג").
 * - a03-F7: at most ONE sold-out row keeps its relevance rank in the six
 *   rows; any further sold-out rows go below the buyable ones. The 26 Sep
 *   rule still holds (an exact sold-out match like "תות" stays visible); the
 *   change stops a second and third sold-out row from pushing buyable
 *   products out. Overlay only: /search keeps the Search & Discovery order.
 * - a03-F5: the results area renders ABOVE the browse doors, so a no-match
 *   message is the first thing under the input, followed by the doors as the
 *   way forward. No "all results" link on a no-match (it opened the same
 *   empty page).
 * - a12-F5: the browse doors (12 maker logos + the YOJO mark) are not
 *   rendered until the overlay first opens (.was-opened, see the CSS).
 * - a10-F8: the input is focused inside the tap handler, so iOS can raise
 *   the keyboard (a focus() from a timer is outside the user gesture).
 * - T-S23 (v03-J6-09): Back (Android key, iOS swipe-back) closes the open
 *   search instead of leaving the page. Same tokened history step as the
 *   quick-add sheet (assets/yojo-quick-add-selector.js); see HISTORY_MARK.
 * - T-S24 (v10-V10-25): the "all results" link quotes the query with ASCII
 *   quotation marks, not gershayim.
 */
(function() {
  'use strict';

  var overlay = document.getElementById('yojo-search-overlay');
  if (!overlay) return;

  var input = overlay.querySelector('[data-search-input]');
  var defaultContent = overlay.querySelector('[data-search-default]');
  var resultsContainer = overlay.querySelector('[data-search-results]');
  var statusEl = overlay.querySelector('[data-search-status]');
  var closeButtons = overlay.querySelectorAll('[data-search-close]');
  // Copy comes from locales/he.json via data-i18n-* on the overlay root
  // (snippets/search-overlay.liquid). The literals below are last-resort only.
  var I18N = {
    noResults: overlay.getAttribute('data-i18n-no-results') || 'אין תוצאות',
    unavailable: overlay.getAttribute('data-i18n-unavailable') ||
      'החיפוש המהיר לא זמין כרגע. אפשר להקיש Enter.'
  };
  var searchForm = overlay.querySelector('.yojo-search-overlay__form');
  var pageSearchUrl = (searchForm && searchForm.getAttribute('action')) || '/search';
  var debounceTimer = null;

  // a03-F5: results (and the no-match message) sit above the browse doors.
  // Moved once here rather than in the snippet so the two blocks keep one
  // owner; the browse block is shown again under a no-match as the way on.
  if (defaultContent && resultsContainer && defaultContent.parentNode === resultsContainer.parentNode) {
    defaultContent.parentNode.insertBefore(resultsContainer, defaultContent);
  }

  var DEBOUNCE_MS = 300;
  var FALLBACK_SECTION_ID = 'main-search';
  var FALLBACK_LIMIT = 6;
  // a03-F7: how many sold-out rows may keep their relevance rank in the six.
  var SOLD_OUT_IN_RANK = 1;
  // Same value the search forms submit (snippets/search-overlay.liquid,
  // sections/main-search.liquid): sold-out products keep their relevance rank.
  var UNAVAILABLE_PARAM = '&options%5Bunavailable_products%5D=show';

  // Monotonic token — bumped on every dispatch (and on close/reset) and
  // checked by every async continuation, so a slow earlier response can
  // never overwrite the results of a newer query.
  var requestToken = 0;

  // Cache of the last successful SRA fallback parse, keyed by query.
  var fallbackCache = { query: null, items: null };

  var searchUrl = window.KROWN && KROWN.settings && KROWN.settings.routes
    ? KROWN.settings.routes.predictive_search_url
    : '/search/suggest.json';

  var lastTrigger = null;

  /* -------------------------------------------------------------------------
     Swarm 2026-10-02 (T-S23, v03-J6-09): BACK CLOSES THE OPEN SEARCH.

     Opening the overlay added no history entry, so Android Back (and iOS
     swipe-back) left the page while the search was open; in an Instagram or
     Facebook webview that closes the browser. This reuses the quick-add
     sheet's tokened pattern (assets/yojo-quick-add-selector.js, a10-F4):
     - Opening pushes one same-URL entry. It carries the page's searchParams,
       so the facet filter's popstate handler (component-facets.js) sees no
       change, and no yhcsCat, so the homepage shelf (yojo-cat-shelf.js)
       ignores it.
     - Back lands on the entry below ours: the overlay closes.
     - x, Esc and the backdrop step back over our entry, only while it is the
       current one, so history is where it was before the search opened.
     - Forward into that spent entry is stepped past at once.
     - Following a row, a door, the "all results" link or Enter replaces our
       entry with the new page (location.replace), so Back from that page
       comes straight to this page, with the overlay closed, and no dead
       step is left behind. A page restored from the back-forward cache
       with the overlay still painted open closes it (pageshow).
     - A re-open before close()'s own step back has landed waits for it,
       then takes a fresh entry.
     The page under the overlay is scroll-locked at every width
     (search-overlay.css, yojo-design-system.css), so the step back restores
     the same scroll position: no jump, unlike the unlocked desktop popover
     that made quick-add go phone-only.
     --------------------------------------------------------------------- */
  var HISTORY_MARK = 'search';
  var historyToken = null; // our entry's token while it is the one on top
  var spentToken = null; // the last entry we stepped back over
  var backPending = false; // our own history.back() has not landed yet

  function pushHistory() {
    if (historyToken || backPending || !window.history || typeof history.pushState !== 'function') return;
    if (window.Shopify && Shopify.designMode) return;
    var token = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    try {
      history.pushState({ yojoOverlay: HISTORY_MARK, yojoOverlayToken: token, searchParams: window.location.search.slice(1) }, '');
      historyToken = token;
    } catch (e) {
      /* sandboxed frame: the overlay works without the Back step */
    }
  }

  function stepBack() {
    backPending = true;
    try { history.back(); } catch (e) { backPending = false; }
  }

  function popHistory() {
    if (!historyToken) return;
    var token = historyToken;
    historyToken = null;
    spentToken = token;
    if (history.state && history.state.yojoOverlayToken === token) stepBack();
  }

  /* Leave for another page from inside the open overlay: the new page takes
     our entry instead of stacking on top of it. */
  function leaveTo(href) {
    historyToken = null;
    window.location.replace(href);
  }

  window.addEventListener('popstate', function (e) {
    var token = e.state && e.state.yojoOverlay === HISTORY_MARK ? e.state.yojoOverlayToken : null;
    if (backPending) {
      backPending = false;
      // Our own step back has landed. A search re-opened meanwhile gets its entry now.
      if (overlay.classList.contains('is-open') && !historyToken) pushHistory();
      return;
    }
    if (!historyToken) {
      if (token && token === spentToken) stepBack();
      return;
    }
    if (token === historyToken) return;
    spentToken = historyToken;
    historyToken = null; // the browser already left our entry
    if (overlay.classList.contains('is-open')) close(false, true);
  });

  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    backPending = false;
    if (!overlay.classList.contains('is-open')) return;
    if (historyToken && history.state && history.state.yojoOverlayToken === historyToken) return;
    historyToken = null;
    close(true, true);
  });

  /* -------------------------------------------------------------------------
     R4 A4 (2026-08-03) — SUGGEST-ENDPOINT CIRCUIT BREAKER.

     Measured on the canonical preview (theme 150882386073 asserted in-page),
     typing "מנגו" produced exactly two requests per debounced keystroke:

       417  /search/suggest?q=…&resources[type]=product,collection,query
                            &resources[limit]=6&section_id=helper-predictive-search
       200  /search?q=…&options[prefix]=last&section_id=main-search

     …plus one "Failed to load resource: 417" in the console every time.

     The parameters are NOT the cause. Seven variants were fetched same-origin
     from the live preview page and every one returned 417, including the
     minimal `/search/suggest?q=…&section_id=helper-predictive-search` and the
     plain `/search/suggest.json` form:

       bare section_id .................... 417
       + resources[limit] ................. 417
       + resources[type]=product .......... 417
       + product,query .................... 417
       + product,collection ............... 417
       + product,collection,query ......... 417   (what shipped)
       .json + resources .................. 417

     So predictive search is unavailable at the platform level for this shop,
     and no request shape rescues it. The existing fallback is correct and
     works (5 real rows rendered, "נמצאו 5 תוצאות" announced) — the defect is
     that the known-dead request was re-fired on every single keystroke, buying
     a guaranteed-failed round trip and a console error before every result.

     Now it is attempted at most once per browsing session. If it fails, the
     overlay goes straight to the Section-Rendering fallback for the rest of
     the session. sessionStorage rather than localStorage so the probe repeats
     next visit and this self-heals the moment Shopify re-enables the endpoint;
     an in-memory flag covers private mode and blocked storage.
     --------------------------------------------------------------------- */
  var SUGGEST_DEAD_KEY = 'yojo:predictive-search-unavailable';
  var suggestDeadMemo = false;

  function suggestIsKnownDead() {
    if (suggestDeadMemo) return true;
    try {
      return window.sessionStorage.getItem(SUGGEST_DEAD_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markSuggestDead() {
    suggestDeadMemo = true;
    try {
      window.sessionStorage.setItem(SUGGEST_DEAD_KEY, '1');
    } catch (e) {
      /* private mode / storage disabled — the in-memory flag still holds for
         this page, which is where the per-keystroke cost was being paid. */
    }
  }

  function open() {
    // Remember the element that opened the overlay so Esc/close can return
    // focus to it (a11y: focus must not fall to <body>).
    if (document.activeElement && document.activeElement !== document.body) {
      lastTrigger = document.activeElement;
    }
    // a12-F5: the browse doors (and their logo images) render from the first
    // open on; until then the closed panel downloads none of them.
    overlay.classList.add('was-opened', 'is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-overlay-open');
    // T-S23: one history entry, so Back closes the search (see HISTORY_MARK).
    pushHistory();
    // a10-F8: focus inside the tap's own handler. iOS Safari raises the
    // keyboard only for a focus() made during the user gesture; the old
    // 350 ms timer ran outside it. `visibility` flips synchronously with the
    // class above, so the input is focusable now. The timer stays as a
    // fallback for an engine that refuses the early focus.
    try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    setTimeout(function() {
      if (overlay.classList.contains('is-open') && document.activeElement !== input) input.focus();
    }, 350);

    /* Audit F1 (2026-08-14): the circuit breaker was probed lazily, on the
       FIRST QUERY of the session — so the user's very first search of every
       visit paid the guaranteed-417 round trip (Hebrew is not on Shopify's
       Predictive Search supported-language list; documented platform fact)
       before the SRA fallback even started. Pre-warm the probe the moment
       the overlay opens: by the time a human has typed two characters plus
       the 300ms debounce, the breaker state is already known and the first
       query goes straight down the healthy path. Self-healing is preserved —
       the probe still runs once per session, so if Shopify ever adds Hebrew
       support the suggest path resumes on its own. */
    if (!suggestIsKnownDead() && !window.__yojoSuggestProbing) {
      window.__yojoSuggestProbing = true;
      fetch(searchUrl + '?q=a&resources[type]=product&resources[limit]=1')
        .then(function (res) { if (!res.ok) markSuggestDead(); })
        .catch(function () { markSuggestDead(); });
    }
  }

  // keepHistory: the browser already left our entry (Back, or a page
  // restored from the back-forward cache), so there is nothing to step over.
  function close(skipFocusReturn, keepHistory) {
    if (!keepHistory) popHistory();
    requestToken++; // invalidate any in-flight responses
    clearTimeout(debounceTimer);
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-overlay-open');
    input.value = '';
    showDefault();
    // NAV-6 (2026-11-02): the panel is only hidden, never reset, so a search that
    // overflowed it (scrollTop 10, input row clipped) reopened already scrolled.
    var panel = overlay.querySelector('.yojo-search-overlay__panel');
    if (panel) panel.scrollTop = 0;
    if (!skipFocusReturn && lastTrigger && document.contains(lastTrigger)) {
      lastTrigger.focus();
    }
    lastTrigger = null;
  }

  function announce(message) {
    if (statusEl) statusEl.textContent = message || '';
  }

  function announceCount(count) {
    announce(count === 1 ? 'נמצאה תוצאה אחת' : 'נמצאו ' + count + ' תוצאות');
  }

  function clearResults() {
    while (resultsContainer.firstChild) {
      resultsContainer.removeChild(resultsContainer.firstChild);
    }
  }

  function showDefault() {
    defaultContent.style.display = '';
    resultsContainer.style.display = 'none';
    resultsContainer.classList.remove('is-empty');
    clearResults();
    announce('');
  }

  function showResults(sourceElement) {
    defaultContent.style.display = 'none';
    resultsContainer.style.display = '';
    resultsContainer.classList.remove('is-empty');
    clearResults();
    // Clone server-rendered nodes (from Shopify's helper section)
    var children = sourceElement.childNodes;
    for (var i = 0; i < children.length; i++) {
      resultsContainer.appendChild(children[i].cloneNode(true));
    }
    var itemCount = sourceElement.querySelectorAll('[data-js-search-item]').length;
    if (itemCount > 0) {
      announceCount(itemCount);
    } else {
      // The helper section's own empty state is showing.
      announce(I18N.noResults);
    }
  }

  /**
   * "All results" link — always the first element of any fallback render
   * (PR-A #223 kept as the floor; PR-F keeps it on top of the SRA rows).
   */
  function buildAllResultsLink(query) {
    var link = document.createElement('a');
    link.href = pageSearchUrl + '?q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last'
      + UNAVAILABLE_PARAM;
    link.className = 'yjs-fallback__all';
    // T-S24 (v10-V10-25): ASCII quotation marks around the query; gershayim
    // (״) belong inside Hebrew abbreviations, not around a quote.
    link.textContent = 'לכל התוצאות ל"' + query + '"';
    return link;
  }

  /**
   * PR-A/#223 floor: link + hint. Used when the SRA fallback itself fails,
   * and as the graceful-empty state when /search returns 0 cards. All user
   * text is set via textContent (never innerHTML) so the query cannot
   * inject markup.
   */
  function showFallback(query) {
    defaultContent.style.display = 'none';
    resultsContainer.style.display = '';
    resultsContainer.classList.remove('is-empty');
    clearResults();
    resultsContainer.appendChild(buildAllResultsLink(query));
    var hint = document.createElement('p');
    hint.className = 'yjs-fallback__hint';
    hint.textContent = I18N.unavailable;
    resultsContainer.appendChild(hint);
    announce(hint.textContent);
  }

  /**
   * A genuine no-match: the request SUCCEEDED and the catalogue simply has
   * nothing. Distinct from showFallback(), which means "the search request
   * failed". Conflating the two told every customer who mistyped that the
   * store's search was broken (2026-08-01 nav-taxonomy audit).
   *
   * The browse doors stay visible underneath, so a dead end still offers the
   * six categories and the makers as a way forward.
   *
   * a03-F5 (swarm 2026-10-01): the message renders first (the results area
   * now sits above the doors), so on a phone it is under the input instead of
   * 874 px down a 564 px panel. The "all results" link is gone here: /search
   * answers the same zero, so it was a link to the same dead end. The doors
   * below are the next step.
   */
  function showEmpty() {
    resultsContainer.style.display = '';
    resultsContainer.classList.add('is-empty');
    clearResults();
    var p = document.createElement('p');
    p.className = 'yjs-fallback__hint yjs-empty';
    p.textContent = I18N.noResults;
    resultsContainer.appendChild(p);
    defaultContent.style.display = '';
    announce(p.textContent);
  }

  /**
   * Extract up to FALLBACK_LIMIT product entries from the SRA render of
   * sections/main-search.liquid. Live fact (2026-07-02): section_id renders
   * with DEFAULT section settings and NO blocks, so a card has no title or
   * price nodes — the product title lives on the card link's aria-label
   * (with the image alt as backup). Everything extracted here comes from
   * our own server-rendered same-origin document.
   */
  function parseFallbackItems(doc, query) {
    var items = [];
    var grid = doc.querySelector('#main-collection-product-grid');
    if (!grid) return items;
    // Canonical card (consolidation 2026-07-31): .yhp-card replaced the
    // retired .product-item. The canonical anatomy always renders a real
    // title node (.yhp-name), so prefer it over the aria-label fallback.
    var cards = grid.querySelectorAll('.yhp-card');
    for (var i = 0; i < cards.length; i++) {
      var link = cards[i].querySelector('a[href]');
      if (!link) continue;
      var img = cards[i].querySelector('img');
      var nameEl = cards[i].querySelector('.yhp-name');
      var title = (nameEl && nameEl.textContent.trim()) ||
        link.getAttribute('aria-label') || (img && img.getAttribute('alt')) || '';
      // A sold-out card (snippets/yojo-product-card.liquid) carries
      // .yojo-card--sold-out and says so in its badge. Reuse that text so the
      // row states the same thing the card does, in the same words.
      var isSoldOut = cards[i].classList.contains('yojo-card--sold-out');
      var soldOut = '';
      if (isSoldOut) {
        var badge = cards[i].querySelector('.yhp-badge');
        soldOut = badge ? badge.textContent.trim() : '';
      }
      items.push({
        url: link.getAttribute('href'),
        title: title,
        soldOut: soldOut,
        isSoldOut: isSoldOut,
        price: readPrice(cards[i]),
        imageSrc: img ? img.getAttribute('src') : '',
        imageSrcset: img ? img.getAttribute('srcset') : ''
      });
    }
    return pickRows(prefixFirst(query, items));
  }

  /**
   * D-1 (journey upgrade 2026-10-21, sweep C-3): products whose title starts
   * with the typed query come first — same rule the suggest path's section
   * (sections/helper-predictive-search.liquid) applies server-side, so both
   * overlay data paths rank an exact title match ("תות" -> תות קפוא) on top.
   * Stable: within each half, /search's own order is kept, so a query with no
   * title-prefix match renders exactly as before.
   */
  function prefixFirst(query, items) {
    var q = tidy(query).toLowerCase();
    if (!q) return items;
    var heads = [];
    var rest = [];
    for (var i = 0; i < items.length; i++) {
      var t = tidy(items[i].title).toLowerCase();
      (t.indexOf(q) === 0 ? heads : rest).push(items[i]);
    }
    return heads.concat(rest);
  }

  /**
   * a03-F7: the six rows, in /search's own order, except that only the first
   * SOLD_OUT_IN_RANK sold-out product keeps its place. Later sold-out
   * products move below every buyable one, so they fill a row only when
   * fewer than six buyable products matched (a query that matches only
   * sold-out products, like the kombucha pair, still shows them).
   */
  function pickRows(all) {
    var rows = [];
    var deferred = [];
    var kept = 0;
    for (var i = 0; i < all.length && rows.length < FALLBACK_LIMIT; i++) {
      if (all[i].isSoldOut && kept >= SOLD_OUT_IN_RANK) {
        deferred.push(all[i]);
        continue;
      }
      if (all[i].isSoldOut) kept++;
      rows.push(all[i]);
    }
    for (var j = 0; j < deferred.length && rows.length < FALLBACK_LIMIT; j++) {
      rows.push(deferred[j]);
    }
    return rows;
  }

  /**
   * a03-F6 / H16: the card's own price line. The /search render already
   * carries it (.yhp-price: amount, an optional <del> compare-at, and the
   * unit in an <i>); the parser used to drop it. Whole-shekel amounts lose
   * ".00" so the row reads like the card ruling of 26 Sep ("21 ₪ · 0.5 ק״ג")
   * whichever money format the card prints. The compare-at is left out: the
   * row states the price the shopper pays.
   */
  function readPrice(card) {
    var el = card.querySelector('.yhp-price');
    if (!el) return null;
    var c = el.cloneNode(true);
    var unitEl = c.querySelector('i, .yhp-price__unit');
    var unit = unitEl ? tidy(unitEl.textContent).replace(/^[·•]\s*/, '') : '';
    var drop = c.querySelectorAll('i, .yhp-price__unit, del, .visually-hidden');
    for (var k = 0; k < drop.length; k++) {
      if (drop[k].parentNode) drop[k].parentNode.removeChild(drop[k]);
    }
    var amount = tidy(c.textContent).replace(/(\d)\.00(?!\d)/g, '$1');
    if (!amount) return null;
    return { amount: amount, unit: unit };
  }

  function tidy(s) {
    return String(s || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Render extracted /search results as rows using the same markup the
   * success path (helper-predictive-search) produces, so the existing
   * overlay styles apply and the fallback is visually indistinguishable
   * from working predictive results. Built entirely with createElement +
   * textContent — no HTML string interpolation.
   */
  function renderFallbackResults(query, items) {
    if (!items || items.length === 0) {
      // Reached only after a 200 — the scrape worked and found nothing.
      showEmpty(query);
      return;
    }
    defaultContent.style.display = 'none';
    resultsContainer.style.display = '';
    resultsContainer.classList.remove('is-empty');
    clearResults();
    resultsContainer.appendChild(buildAllResultsLink(query));

    // NOT class "search-results": component-sidebar.css styles that class
    // globally (max-height + overflow scroll).
    var list = document.createElement('div');
    list.className = 'yjs-fallback__list';
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var row = document.createElement('div');
      row.className = 'search-item cart-item';

      var thumbLink = document.createElement('a');
      thumbLink.className = 'cart-item__thumbnail element--border-width-clamped element--border-radius';
      thumbLink.setAttribute('href', item.url);
      // Decorative duplicate of the title link right next to it.
      thumbLink.setAttribute('tabindex', '-1');
      thumbLink.setAttribute('aria-hidden', 'true');
      if (item.imageSrc) {
        var img = document.createElement('img');
        img.setAttribute('src', item.imageSrc);
        if (item.imageSrcset) img.setAttribute('srcset', item.imageSrcset);
        img.setAttribute('sizes', '56px');
        img.setAttribute('alt', '');
        img.setAttribute('loading', 'lazy');
        thumbLink.appendChild(img);
      }

      var content = document.createElement('div');
      content.className = 'cart-item__content';
      var titleLink = document.createElement('a');
      titleLink.className = 'cart-item__title';
      titleLink.setAttribute('href', item.url);
      var titleSpan = document.createElement('span');
      titleSpan.className = 'text-animation--underline-thin';
      titleSpan.textContent = item.title;
      titleLink.appendChild(titleSpan);
      if (item.soldOut) {
        // Same "title (state)" form as the card's own accessible name.
        var state = document.createElement('span');
        state.className = 'text-size--small text-color--opacity';
        state.setAttribute('data-search-sold-out', '');
        state.textContent = ' (' + item.soldOut + ')';
        titleLink.appendChild(state);
      }
      if (item.price) {
        // Inside the link, so the row's accessible name carries the price too.
        var price = document.createElement('span');
        price.className = 'yjs-row__price';
        var amount = document.createElement('span');
        amount.className = 'yjs-row__amount';
        amount.textContent = item.price.amount;
        price.appendChild(amount);
        if (item.price.unit) {
          var unit = document.createElement('span');
          unit.className = 'yjs-row__unit';
          unit.textContent = ' · ' + item.price.unit;
          price.appendChild(unit);
        }
        titleLink.appendChild(price);
      }
      content.appendChild(titleLink);

      row.appendChild(thumbLink);
      row.appendChild(content);
      list.appendChild(row);
    }
    resultsContainer.appendChild(list);
    announceCount(items.length);
  }

  /**
   * Section-Rendering fallback on the healthy /search route. Fires only
   * after the suggest endpoint failed. options[prefix]=last is included
   * for parity with the form submit, NOT because it fixes Hebrew partials
   * (live-verified: it does not).
   */
  function fetchFallbackResults(query, token) {
    if (fallbackCache.query === query && fallbackCache.items) {
      renderFallbackResults(query, fallbackCache.items);
      return;
    }
    /* Audit F1 (2026-08-14): between the keystroke and the SRA response the
       panel showed nothing (and on a failure-turned-retry, the "unavailable"
       floor) — a quiet gap that read as broken search. Announce that a
       search is actually happening; every terminal state overwrites it. */
    announce('מחפשים…');
    var url = pageSearchUrl + '?q=' + encodeURIComponent(query)
      + '&options%5Bprefix%5D=last'
      + UNAVAILABLE_PARAM
      + '&section_id=' + FALLBACK_SECTION_ID;

    fetch(url)
      .then(function(res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function(text) {
        if (token !== requestToken) return; // stale — drop
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var items = parseFallbackItems(doc, query);
        fallbackCache = { query: query, items: items };
        renderFallbackResults(query, items);
      })
      .catch(function() {
        if (token !== requestToken) return; // stale — drop
        // Even the fallback failed: PR-A/#223 floor (link + hint).
        showFallback(query);
      });
  }

  function fetchResults(query, token) {
    // Circuit breaker: the suggest endpoint answered 417 earlier in this
    // session, so skip it entirely instead of re-failing per keystroke.
    if (suggestIsKnownDead()) {
      fetchFallbackResults(query, token);
      return;
    }

    var url = searchUrl + '?q=' + encodeURIComponent(query)
      + '&resources[type]=product,collection,query'
      + '&resources[limit]=6'
      + '&resources[options][unavailable_products]=show'
      + '&section_id=helper-predictive-search';

    fetch(url)
      .then(function(res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function(text) {
        if (token !== requestToken) return; // stale — drop
        var parser = new DOMParser();
        var doc = parser.parseFromString(text, 'text/html');
        var sectionEl = doc.querySelector('#shopify-section-helper-predictive-search');
        var holder = sectionEl && sectionEl.querySelector('[data-js-search-results-holder]');
        if (holder && holder.children.length > 0) {
          showResults(holder);
        } else {
          showEmpty(query);
        }
      })
      .catch(function() {
        // Non-200s throw above, so network errors AND bad statuses (the
        // live 417) land here → Section-Rendering fallback on /search.
        // Trip the breaker so the rest of this session skips the probe.
        markSuggestDead();
        if (token !== requestToken) return; // stale — drop
        fetchFallbackResults(query, token);
      });
  }

  input.addEventListener('input', function() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function() {
      var query = input.value.trim();
      var token = ++requestToken; // invalidates all in-flight responses
      if (!query || query.length < 2) {
        showDefault();
        return;
      }
      fetchResults(query, token);
    }, DEBOUNCE_MS);
  });

  overlay.querySelector('.yojo-search-overlay__form').addEventListener('submit', function(e) {
    if (!input.value.trim()) {
      e.preventDefault();
      return;
    }
    // T-S23: Enter opens /search in place of our history entry (leaveTo).
    var form = e.currentTarget;
    if (!historyToken || e.defaultPrevented || String(form.method).toLowerCase() !== 'get') return;
    var target;
    try {
      target = new URL(form.action, window.location.href);
      target.search = new URLSearchParams(new FormData(form)).toString();
    } catch (err) {
      return; // the browser's own submit still works; it just stacks a step
    }
    e.preventDefault();
    leaveTo(target.href);
  });

  /* T-S23: a row, a door or the "all results" link opens its page in place
     of our history entry, so Back from it lands on this page in one step.
     Plain left clicks and taps only: a new-tab or modified click leaves this
     tab (and its entry) alone. */
  overlay.addEventListener('click', function(e) {
    if (!historyToken || e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var link = e.target.closest && e.target.closest('a[href]');
    if (!link || !overlay.contains(link) || link.hasAttribute('download')) return;
    if (link.target && link.target !== '_self') return;
    if (link.origin !== window.location.origin) return;
    if (link.hash && link.pathname === window.location.pathname && link.search === window.location.search) return;
    e.preventDefault();
    leaveTo(link.href);
  });

  closeButtons.forEach(function(btn) {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      close();
    });
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
      close();
    }
  });

  /* -------------------------------------------------------------------------
     R4 A4 (2026-08-03) — FOCUS CONTAINMENT FOR A REAL MODAL.

     snippets/search-overlay.liquid declares role="dialog" aria-modal="true".
     aria-modal is a promise to assistive tech that nothing outside this
     element is reachable, but nothing enforced it: measured on the preview,
     the overlay opened over a live page with no inert/aria-hidden barrier and
     no Tab handling, so Tab walked straight past the last tile and back into
     the page behind — the announced model and the keyboard model disagreed.

     This is a GENUINE dialog (it takes over the viewport and owns the task),
     so containment is the correct fix here and the correct fix is NOT to be
     copied to the consent bar, which is non-modal by contract (C11) and must
     keep letting Tab pass through.

     Esc and click-outside still dismiss, and close() already restores focus to
     the opener, so the trap is always escapable.
     --------------------------------------------------------------------- */
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function overlayFocusables() {
    return Array.prototype.filter.call(
      overlay.querySelectorAll(FOCUSABLE_SELECTOR),
      function(el) {
        // Decorative duplicates (the row thumbnails) opt out with tabindex -1.
        if (el.getAttribute('tabindex') === '-1') return false;
        // Skip anything the overlay has hidden (the browse tiles while results
        // are showing, and vice versa) — offsetParent is null for those.
        return el.offsetParent !== null || el === document.activeElement;
      }
    );
  }

  /* Swarm 2026-10-01: the trap now moves Tab / Shift+Tab itself through the
     dialog's own items. It used to step in only at the two ends and leave the
     middle to the browser, but Safari (default setting) does not Tab to
     links, so the "last" end (a door link) was never reached: measured on
     WebKit 375, Tab from the close button left the page for the browser's
     own controls. Same order as the DOM, so Chromium behaves as before; in
     Safari the rows and doors are now reachable by Tab inside the dialog.
     The shared trap (assets/yojo-a11y-focus-trap.js) also guards this dialog
     and runs first, in the capture phase: when it has already wrapped at an
     end it prevents the default, and stepping again here would skip the
     input going forward and the last item going back. */
  document.addEventListener('keydown', function(e) {
    if (e.key !== 'Tab' || e.defaultPrevented) return;
    if (!overlay.classList.contains('is-open')) return;

    var items = overlayFocusables();
    if (!items.length) return;

    e.preventDefault();
    var i = items.indexOf(document.activeElement);
    var next;
    if (i === -1) {
      next = e.shiftKey ? items.length - 1 : 0;
    } else if (e.shiftKey) {
      next = i === 0 ? items.length - 1 : i - 1;
    } else {
      next = i === items.length - 1 ? 0 : i + 1;
    }
    items[next].focus();
  });

  /* Swarm 2026-10-01: and focus moved out of the open dialog by anything
     else (a script, a late banner) comes back to the input. Capture phase,
     so a widget that stops focusin propagation on its own nodes cannot hide
     the move from us. */
  document.addEventListener('focusin', function(e) {
    if (!overlay.classList.contains('is-open') || overlay.contains(e.target)) return;
    try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
  }, true);

  /* The header triggers open a dialog. `aria-haspopup="dialog"` says so.
     aria-expanded is deliberately NOT set: the ARIA dialog pattern does not
     use it for modal triggers, and adding it here would be the same kind of
     state claim this workstream is removing elsewhere. Set from JS because
     the trigger markup lives in snippets owned by the header. */
  Array.prototype.forEach.call(
    document.querySelectorAll('[data-open-search]'),
    function(trigger) {
      trigger.setAttribute('aria-haspopup', 'dialog');
    }
  );

  document.addEventListener('click', function(e) {
    var trigger = e.target.closest('[data-open-search]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      open();
    }
  });

  var headerSearchHandle = document.querySelector('.header-actions--buttons .site-search-handle');
  if (headerSearchHandle) {
    headerSearchHandle.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      open();
    });
  }

})();
